/**
 * The draft entry: what exists only in this browser, and how it is edited.
 *
 * Everything here operates on `ProvenanceItem[]` and returns a new list, or the
 * same list by identity when there is nothing to do. The shape is the
 * append-never-amend rule made structural — only the `draft` branch carries an
 * editable entry, so no mutator here can reach a recorded one. Document intake
 * and required-field validation live beside the editors because they decide
 * what a draft may hold before it is ever sent.
 */
import type {
  ProvenanceDocument,
  ProvenanceDocumentKind,
  ProvenanceEntry,
  ProvenanceEntryKind,
  ProvenanceEvidenceClaim,
  ProvenanceMethod,
} from "@ondo/shared";

import { ONDO_ID_PATTERN } from "../lib/constants.ts";
import { describeBytes, fileKey } from "../lib/files.ts";
import { parseMoneyMinor } from "../lib/money.ts";
import {
  ACCEPTED_DOCUMENT_TYPES,
  DEFAULT_DOCUMENT_KIND,
  DEFAULT_GRADE,
  DEFAULT_INSURED_CURRENCY,
  DEFAULT_METHOD,
  HISTORY_KINDS,
  MAX_DOCUMENTS_PER_ENTRY,
  MAX_DOCUMENT_BYTES,
  MAX_ENTRIES,
  PROVENANCE_COPY,
} from "./provenance.constants.ts";
import type { HistoryTextKey } from "./provenance.constants.ts";

/**
 * A document chosen on this device and not yet uploaded.
 *
 * The same deferral as `PendingImage`, and for the same reason: the bytes have
 * nothing to attach to until an entry row exists, and an entry row is only
 * opened when the collector commits. An abandoned wizard leaves no entry, no
 * document row and nothing in the bucket.
 */
export interface PendingDocument {
  /** Client-side only. The row's real id is minted by the database on insert. */
  id: string;
  file: File;
  kind: ProvenanceDocumentKind;
  /** '1.4 MB', decided at intake so a row does no arithmetic while rendering. */
  sizeLabel: string;
}

/** A comment written in the wizard. Its id exists so it can be taken back. */
export interface PendingComment {
  id: string;
  body: string;
}

/**
 * Somebody to ask, once there is an entry to ask about.
 *
 * A DISCRIMINATED PARTY rather than two nullable fields, because exactly one of
 * the two ways of naming somebody is ever set and the RPC refuses both and
 * neither. A pair of optional strings would let a caller queue an ask naming
 * nobody, and the first place that would surface is a 422 from Postgres.
 *
 * The two are not interchangeable to the person typing. An ONDO id is something
 * you were GIVEN by an account that is already here; an address is how you
 * reach somebody who may never have heard of ONDO — the shipper, the registrar,
 * the conservator's assistant. What the two have in common is that neither
 * search is possible: ONDO will not tell one account whether another exists, so
 * both are typed from something the asker already had.
 */
export type PendingParty =
  /** Normalised at intake: 'ONDO-1042', never 'ondo-1042 '. */
  | { kind: "ondoId"; value: string }
  /** Lower-cased at intake, so two casings of one address are one ask. */
  | { kind: "email"; value: string };

export interface PendingRequest {
  id: string;
  party: PendingParty;
}

/**
 * What the author has done to their own draft, held here until it is a row.
 *
 * The same deferral as `PendingDocument`, with a sharper edge on one of them: a
 * queued request NAMES a real account, and sending it discloses which work and
 * whose claim. An abandoned wizard must tell nobody, so nothing here is sent
 * before submit and every item can be taken back before it is.
 */
export interface PendingActs {
  /**
   * The author's own position. Deliberately NOT `AttestationStance | null`:
   * dispute is unreachable on a draft — a draft is still editable, so
   * disagreeing with it is an edit — and typing this to the full union would
   * let a writer send a body-less dispute into a check constraint that requires
   * one. Widening it later forces that question to be answered.
   */
  stance: "verify" | null;
  comments: PendingComment[];
  requests: PendingRequest[];
}

/**
 * An entry that exists only in this browser. Freely editable, by definition.
 *
 * EVERY TEXT FIELD IS PRESENT WHATEVER THE KIND, and only the ones
 * `HISTORY_KINDS[kind].fields` names are ever rendered or ever sent. A draft
 * with a field per kind would be eight shapes to write, eight to validate and
 * eight to render, and switching kinds would throw away what had been typed —
 * which is exactly what a collector who picked the wrong chip does not want.
 * `toEntryPayloads` drops anything the chosen kind does not carry, and the API
 * refuses it besides, so nothing typed under a discarded chip can reach the
 * record.
 *
 * The keys match `HistoryTextKey` one for one, asserted below rather than
 * restated — constants may not import this file.
 */
export interface DraftEntry {
  id: string;
  /** Chosen once, in the modal, and never afterwards. */
  kind: ProvenanceEntryKind;

  owner: string;
  counterparty: string;
  venue: string;
  title: string;
  reference: string;
  person: string;
  publisher: string;
  price: string;
  gallery: string;
  location: string;
  insurer: string;
  policy: string;

  /**
   * The sum insured, AS TYPED, and the currency beside it.
   *
   * Text in the draft and a number on the wire: `parseMoneyMinor` turns
   * '1,850,000' into minor units at submit, which is the same division of
   * labour the consignment form uses. A number here would mean the field could
   * not hold a half-typed figure, and a form that refuses keystrokes is worse
   * than one that refuses a submit.
   */
  insuredAmount: string;
  insuredCurrency: string;
  /**
   * The authorship claim in words, filled from the work and never typed.
   *
   * A text key like the ten above it, so the form renders it and
   * `toEntryPayload` sends it through exactly the same table-driven path. What
   * makes it different is `fixed` on its field — see `HistoryField`.
   */
  claimedArtistName: string;

  method: ProvenanceMethod;
  documentKind: ProvenanceDocumentKind;
  startDate: string;
  endDate: string;
  notes: string;
  grade: ProvenanceEvidenceClaim;
  /**
   * Set only when the author explicitly attached one, never inferred from
   * `owner`. There is no directory to look a name up in, and guessing would
   * manufacture a link between a typed string and a real principal.
   */
  ondoId: string | null;
  /**
   * The registry artist the claim POINTS AT — `WorkSummary.artistId`, an
   * `ondo.artists` id.
   *
   * Not a `HistoryTextKey`, because it is not a field: there is no control for
   * it, no label and nothing on screen reading it. It rides beside
   * `claimedArtistName` because the two halves of a claim have to travel
   * together — the id is what makes the claim answerable and the words are what
   * the entry freezes — and `toEntryPayload` sends both or neither.
   *
   * Null on every kind but `attribution`, and null on an `attribution` draft the
   * work could not fill, which is the state the chip is withheld in.
   */
  claimedArtistId: string | null;
  documents: PendingDocument[];
  /** Verified, commented on, asked about — recorded here, filed at submit. */
  acts: PendingActs;
}

/**
 * The work's own artist, as the one thing a `fixed` field is filled from.
 *
 * Both halves of `AddProvenanceEntryRequest`'s authorship claim, read straight
 * off the record the entry is being written onto: `id` is
 * `WorkSummary.artistId` and `name` is `WorkSummary.attributedName`.
 *
 * ONE OBJECT RATHER THAN TWO PARAMETERS, so "an id with no words" and "words
 * with no id" are not states a caller can be in. The API requires both on the
 * kind and refuses either alone, and a surface that holds one and not the other
 * holds nothing it could ask a studio about.
 */
export interface ClaimedArtist {
  /** An `ondo.artists` id. Never an ONDO principal id; it resolves to no account. */
  id: string;
  /** 'Erwin Wurm' — the claim as the record already words it. */
  name: string;
}

/**
 * Whether a kind has anything it cannot be asked to invent.
 *
 * READS THE TABLE AND NOT THE KIND, which is the whole of how this folder
 * avoids a `switch (kind)`: a tenth kind carrying a fixed field is gated by
 * this function on the day it is added to `HISTORY_KINDS`, with no edit here.
 */
export function needsClaimedArtist(kind: ProvenanceEntryKind): boolean {
  return HISTORY_KINDS[kind].fields.some(
    (field) => field.control === "text" && field.fixed === true,
  );
}

/**
 * The claim written onto a draft, for the kind that carries one.
 *
 * Called on every kind change rather than only on the first, so switching to
 * `attribution` and away again cannot leave a claim on a kind the API refuses
 * it on — `addProvenanceEntrySchema` is `.strict()`, and a stray
 * `claimedArtist` would 400 the whole entry rather than be ignored. The clearing
 * arm is what makes "switching chips mid-form is safe" stay true for the ninth
 * kind.
 *
 * A null claim on a kind that wants one leaves the field blank, which
 * `validateEntry` then refuses by name. That path is unreachable through the
 * chip row — the chip is withheld — and is left refusable rather than thrown,
 * because the one thing worse than a disabled chip is a silently empty claim
 * reaching a studio.
 */
export function withClaimedArtist(
  entry: DraftEntry,
  claim: ClaimedArtist | null,
): DraftEntry {
  if (!needsClaimedArtist(entry.kind)) {
    return { ...entry, claimedArtistId: null, claimedArtistName: "" };
  }

  return { ...entry, claimedArtistId: claim?.id ?? null, claimedArtistName: claim?.name ?? "" };
}

/**
 * The compile-time tie between the field table and the draft it writes into.
 *
 * A key added to `HistoryTextKey` with no property here stops the build, which
 * is the only way the two stay in step — the table lives in constants and
 * cannot name this type.
 */
type _DraftCoversEveryTextKey = Record<HistoryTextKey, string> extends Pick<
  DraftEntry,
  HistoryTextKey
>
  ? true
  : never;
const _draftCoversEveryTextKey: _DraftCoversEveryTextKey = true;
void _draftCoversEveryTextKey;

/**
 * The two states an entry can be in, made unrepresentable in the wrong
 * combination.
 *
 * This is the append-never-amend rule turned structural. An editable form can
 * only be built from a `draft` and a read view only from a `recorded`, so there
 * is no shape a caller could hand to a mutator that would rewrite a persisted
 * entry — and `setField` on a recorded id is not a guard that can be forgotten,
 * it is a branch with nothing in it.
 */
export type ProvenanceItem =
  | { kind: "draft"; entry: DraftEntry }
  | { kind: "recorded"; entry: ProvenanceEntry; documents: ProvenanceDocument[] };

export interface DocumentIntake {
  accepted: File[];
  /** One sentence per refusal, in the order the files arrived. */
  rejected: string[];
}

/**
 * A blank entry of one kind, at the weakest grade.
 *
 * `sale` is the default parameter and not a default CHOICE: the register wizard
 * calls this with no argument because its step 3 is the chain of title and
 * nothing else, and the modal never calls it until a chip has been pressed.
 * Nothing anywhere files an entry under a kind nobody picked.
 */
export function emptyEntry(
  kind: ProvenanceEntryKind = "sale",
  /**
   * The work's artist, for the kind whose claim is read off the record.
   *
   * Defaults to null so the register wizard and every existing caller are
   * unchanged. A null claim on `attribution` is a blank claim and is refused by
   * `validateEntry`; the chip row never offers that combination.
   */
  claim: ClaimedArtist | null = null,
): DraftEntry {
  return withClaimedArtist(blankEntry(kind), claim);
}

function blankEntry(kind: ProvenanceEntryKind): DraftEntry {
  return {
    id: crypto.randomUUID(),
    kind,
    owner: "",
    counterparty: "",
    venue: "",
    title: "",
    reference: "",
    person: "",
    publisher: "",
    price: "",
    gallery: "",
    location: "",
    insurer: "",
    policy: "",
    insuredAmount: "",
    insuredCurrency: DEFAULT_INSURED_CURRENCY,
    claimedArtistName: "",
    claimedArtistId: null,
    method: DEFAULT_METHOD,
    documentKind: DEFAULT_DOCUMENT_KIND,
    startDate: "",
    endDate: "",
    notes: "",
    grade: DEFAULT_GRADE,
    ondoId: null,
    documents: [],
    acts: { stance: null, comments: [], requests: [] },
  };
}

export function addEntry(
  items: ProvenanceItem[],
  kind: ProvenanceEntryKind = "sale",
  claim: ClaimedArtist | null = null,
): ProvenanceItem[] {
  if (items.length >= MAX_ENTRIES) return items;
  return [...items, { kind: "draft", entry: emptyEntry(kind, claim) }];
}

/**
 * The kind changed, and the claim follows it.
 *
 * `setEntryField(items, id, "kind", kind)` is what this replaces, and the
 * difference is the one thing a bare field write cannot do: a claim belongs to
 * the kind rather than to the draft, so moving to `attribution` has to fill it
 * and moving away has to clear it. Doing that at the call site would put the
 * rule in the controller, and there are two controllers.
 *
 * Everything else typed under the old chip is kept, which is `DraftEntry`'s
 * standing promise — a collector who starts a sale and decides it was an
 * auction should not lose the date.
 */
export function setEntryKind(
  items: ProvenanceItem[],
  id: string,
  kind: ProvenanceEntryKind,
  claim: ClaimedArtist | null = null,
): ProvenanceItem[] {
  if (!items.some((item) => item.kind === "draft" && item.entry.id === id)) return items;

  return items.map((item) =>
    item.kind === "draft" && item.entry.id === id
      ? { kind: "draft", entry: withClaimedArtist({ ...item.entry, kind }, claim) }
      : item,
  );
}

export function removeEntry(items: ProvenanceItem[], id: string): ProvenanceItem[] {
  // Drafts only. A recorded entry is withdrawn through the API, which is a
  // different act with a different word for it.
  return items.filter((item) => !(item.kind === "draft" && item.entry.id === id));
}

/**
 * Returns the list unchanged, by identity, for anything that is not a draft.
 *
 * Not a guard so much as the absence of a path: the union carries the editable
 * shape only on the draft branch, so there is nothing here to forget.
 */
export function setEntryField<K extends keyof DraftEntry>(
  items: ProvenanceItem[],
  id: string,
  key: K,
  value: DraftEntry[K],
): ProvenanceItem[] {
  if (!items.some((item) => item.kind === "draft" && item.entry.id === id)) return items;

  return items.map((item) =>
    item.kind === "draft" && item.entry.id === id
      ? { kind: "draft", entry: { ...item.entry, [key]: value } }
      : item,
  );
}

export function addDocuments(
  items: ProvenanceItem[],
  id: string,
  files: File[],
): { items: ProvenanceItem[]; rejected: string[] } {
  const target = items.find((item) => item.kind === "draft" && item.entry.id === id);
  if (!target || target.kind !== "draft") return { items, rejected: [] };

  const intake = screenDocuments(files, target.entry.documents);
  if (intake.accepted.length === 0) return { items, rejected: intake.rejected };

  const added: PendingDocument[] = intake.accepted.map((file) => ({
    id: crypto.randomUUID(),
    file,
    kind: DEFAULT_DOCUMENT_KIND,
    sizeLabel: describeBytes(file.size),
  }));

  return {
    items: setEntryField(items, id, "documents", [...target.entry.documents, ...added]),
    rejected: intake.rejected,
  };
}

export function setDocumentKind(
  items: ProvenanceItem[],
  entryId: string,
  documentId: string,
  kind: ProvenanceDocumentKind,
): ProvenanceItem[] {
  const target = items.find((item) => item.kind === "draft" && item.entry.id === entryId);
  if (!target || target.kind !== "draft") return items;

  return setEntryField(
    items,
    entryId,
    "documents",
    target.entry.documents.map((document) =>
      document.id === documentId ? { ...document, kind } : document,
    ),
  );
}

export function removeDocument(
  items: ProvenanceItem[],
  entryId: string,
  documentId: string,
): ProvenanceItem[] {
  const target = items.find((item) => item.kind === "draft" && item.entry.id === entryId);
  if (!target || target.kind !== "draft") return items;

  return setEntryField(
    items,
    entryId,
    "documents",
    target.entry.documents.filter((document) => document.id !== documentId),
  );
}

/**
 * Attaches an ONDO id the author already knows.
 *
 * Returns the problem sentence rather than throwing, and NEVER checks whether
 * the id exists. There is no endpoint that would answer, deliberately: a form
 * that reported liveness would be a directory probe, and the server gives the
 * same refusal for "no such id" and "not permitted" for the same reason.
 */
export function attachOndoId(
  items: ProvenanceItem[],
  id: string,
  raw: string,
): { items: ProvenanceItem[]; problem: string | null } {
  const normalised = raw.trim().toUpperCase();
  if (!ONDO_ID_PATTERN.test(normalised)) {
    return { items, problem: PROVENANCE_COPY.attachInvalid };
  }

  return { items: setEntryField(items, id, "ondoId", normalised), problem: null };
}

export function detachOndoId(items: ProvenanceItem[], id: string): ProvenanceItem[] {
  return setEntryField(items, id, "ondoId", null);
}

/**
 * Which of the dropped files may be attached, and why the rest may not.
 *
 * Every refusal is checked here rather than at upload time, so a file the bucket
 * would reject is refused while the collector is still looking at the entry —
 * not at the end of a submission. Mirrors `screenImages`, including that it
 * takes plain `File`s so nothing in it touches the DOM.
 */
export function screenDocuments(
  incoming: File[],
  existing: PendingDocument[],
): DocumentIntake {
  const accepted: File[] = [];
  const rejected: string[] = [];
  const seen = new Set(existing.map((document) => fileKey(document.file)));

  for (const file of incoming) {
    if (!isAcceptedType(file.type)) {
      rejected.push(`${file.name} is not a PDF, JPEG, PNG, TIFF, HEIC or WebP.`);
      continue;
    }
    if (file.size > MAX_DOCUMENT_BYTES) {
      rejected.push(`${file.name} is ${describeBytes(file.size)} — the limit is 25MB.`);
      continue;
    }
    if (file.size === 0) {
      rejected.push(`${file.name} is empty.`);
      continue;
    }
    if (seen.has(fileKey(file))) {
      rejected.push(`${file.name} is already attached to this entry.`);
      continue;
    }
    if (existing.length + accepted.length >= MAX_DOCUMENTS_PER_ENTRY) {
      rejected.push(
        `${file.name} was not attached — ${String(MAX_DOCUMENTS_PER_ENTRY)} documents is the most one entry can carry.`,
      );
      continue;
    }

    seen.add(fileKey(file));
    accepted.push(file);
  }

  return { accepted, rejected };
}

function isAcceptedType(type: string): boolean {
  return (ACCEPTED_DOCUMENT_TYPES as readonly string[]).includes(type);
}

/**
 * The first empty required field on this draft, named.
 *
 * Reads the same table the form renders from, so a field that is starred on
 * screen is the field that is refused here — there is no second list of
 * required things to fall out of step with the first. The database has the
 * final word (`provenance_entries_kind_requires`); this exists so the collector
 * is told before they press the button rather than after.
 */
export function validateEntry(entry: DraftEntry): string | null {
  const model = HISTORY_KINDS[entry.kind];

  for (const field of model.fields) {
    if (field.control !== "text" || !field.required) continue;
    if (entry[field.key].trim() === "") return `${strip(field.label)} is needed.`;
  }

  if (model.notes.required && entry.notes.trim() === "") {
    return `${strip(model.notes.label)} is needed.`;
  }

  /**
   * The money field, which the loop above cannot reach: it is not a `text`
   * control and is never `required`.
   *
   * What IS required is the pair. The database refuses a sum with no currency
   * (`provenance_entries_insured_names_its_currency`) and refuses a currency
   * that is not three letters, so both are said here while the form is still
   * open rather than arriving as a 422 about a constraint.
   *
   * A blank amount is fine and common — a record may know who insured a work
   * and not for how much — so this only fires once something has been typed.
   */
  if (model.fields.some((field) => field.control === "money")) {
    const currency = entry.insuredCurrency.trim().toUpperCase();
    const typed = entry.insuredAmount.trim();

    if (typed !== "" && parseMoneyMinor(typed) === null) {
      return PROVENANCE_COPY.insuredAmountInvalid;
    }
    if (typed !== "" && !/^[A-Z]{3}$/.test(currency)) {
      return PROVENANCE_COPY.insuredCurrencyNeeded;
    }
  }

  return null;
}

/** 'New owner *' → 'New owner'. The star is a mark on the label, not a word. */
export function strip(label: string): string {
  return label.replace(/\s*\*$/, "");
}

/**
 * The first problem across every draft, said with its position.
 *
 * Position rather than name, because the entry that fails validation is the one
 * with no name in it — "Entry 3" is the only thing that identifies it.
 */
export function validateEntries(items: ProvenanceItem[]): string | null {
  for (const [index, item] of items.entries()) {
    if (item.kind !== "draft") continue;
    const problem = validateEntry(item.entry);
    if (problem) return `Entry ${String(index + 1)}: ${problem}`;
  }
  return null;
}

/** The first draft that fails validation, so the view can open it. */
export function firstInvalidEntryId(items: ProvenanceItem[]): string | null {
  for (const item of items) {
    if (item.kind === "draft" && validateEntry(item.entry)) return item.entry.id;
  }
  return null;
}
